import { createAction, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { BookingObjectType } from 'src/components/BookingObjectTypeSelect/types';
import { Point } from 'src/components/FloorMap/types';
import { defaultRoomSeatCountByRoomType } from 'src/pages/AdministratorPage/consts';
import { roomTypeByBookingObjectType } from 'src/pages/AdministratorPage/utils';
import {
  BookingObject,
  Desk,
  Parking,
  PlaceType,
  Room,
  RoomType,
} from 'src/types';

import { getIsDesktop, getIsMobileWithTouchscreen } from '../agent/selectors';
import { removeBuilding } from '../buildings/services';
import { removeFloor } from '../floors/services';
import { startAppListening } from '../listenerMiddleware';
import { sidesheetActions } from '../sidesheet/slice';
import { removeTown } from '../towns/services';
import { StoreState } from '../types';

import { initialState } from './initialState';
import { getAdminState } from './selectors';
import {
  fetchBookingObjects,
  fetchBookingObject,
  fetchAdminAllData,
  uploadPlan,
} from './services';

const toInitialDesk = (base: Desk): Desk => ({
  ...base,
  byWindow: false,
  withMonitor: false,
  withCharger: false,
});

const toInitialRoom = (base: Room): Room => ({
  ...base,
  withTv: false,
  withVideoCalls: false,
  withFlipChart: false,
  withWater: false,
});

const toInitialParking = (base: Parking): Parking => ({
  ...base,
});

const toInitialBookingObject = (base: BookingObject): BookingObject => {
  switch (base.type) {
    case PlaceType.Desk:
      return toInitialDesk(base);
    case PlaceType.Room:
      return toInitialRoom(base);
    case PlaceType.Parking:
      return toInitialParking(base);
  }
};

const roundPoint = (point: Point, value: number = 10): Point => ({
  x: Math.round(point.x / value) * value,
  y: Math.round(point.y / value) * value,
});

interface PointToBookingObjectParams {
  point: Point;
  width: number;
  height: number;
  fontSize: number;
  bookingObjectType: BookingObjectType;
  rotation: number;
  mapPlaceId: string;
}

interface PointToDeskParams {
  point: Point;
  width: number;
  height: number;
  fontSize: number;
  rotation: number;
  mapPlaceId: string;
}

const pointToDesk = ({
  point,
  width,
  height,
  fontSize,
  rotation,
  mapPlaceId,
}: PointToDeskParams): Desk => {
  return {
    mapPlaceId,
    id: undefined,
    type: PlaceType.Desk,
    x: point.x,
    y: point.y,
    width,
    height,
    fontSize,
    rotation,
    title: '',
    responsible: null,
    responsibleEmployeeNumber: null,
  };
};

interface PointToRoomParams {
  point: Point;
  width: number;
  height: number;
  fontSize: number;
  rotation: number;
  mapPlaceId: string;
  roomType: RoomType;
}

const pointToRoom = ({
  point,
  width,
  height,
  fontSize,
  mapPlaceId,
  roomType,
}: PointToRoomParams): Room => {
  return {
    mapPlaceId,
    id: undefined,
    type: PlaceType.Room,
    x: point.x,
    y: point.y,
    width,
    height,
    fontSize,
    title: '',
    roomType,
    seatCount: defaultRoomSeatCountByRoomType[roomType],
  };
};

interface PointToParkingParams {
  point: Point;
  width: number;
  height: number;
  fontSize: number;
  rotation: number;
  mapPlaceId: string;
}

const pointToParking = ({
  point,
  width,
  height,
  fontSize,
  mapPlaceId,
  rotation,
}: PointToParkingParams): Parking => {
  return {
    mapPlaceId,
    id: undefined,
    type: PlaceType.Parking,
    x: point.x,
    y: point.y,
    width,
    height,
    fontSize,
    title: '',
    responsible: null,
    responsibleEmployeeNumber: null,
    guest: false,
    shared: false,
    rotation,
  };
};

const pointToBookingObjectByBookingObjectType = {
  [BookingObjectType.Desk]: pointToDesk,
  [BookingObjectType.FocusRoom]: (args: PointToBookingObjectParams) =>
    pointToRoom({
      ...args,
      roomType: roomTypeByBookingObjectType[BookingObjectType.FocusRoom],
    }),
  [BookingObjectType.PhoneRoom]: (args: PointToBookingObjectParams) =>
    pointToRoom({
      ...args,
      roomType: roomTypeByBookingObjectType[BookingObjectType.PhoneRoom],
    }),
  [BookingObjectType.MeetingRoom]: (args: PointToBookingObjectParams) =>
    pointToRoom({
      ...args,
      roomType: roomTypeByBookingObjectType[BookingObjectType.MeetingRoom],
    }),
  [BookingObjectType.Parking]: pointToParking,
};

const pointToBookingObject = (
  args: PointToBookingObjectParams,
): BookingObject | undefined => {
  const adjustedPoint = roundPoint(args.point);

  const mapper =
    pointToBookingObjectByBookingObjectType[args.bookingObjectType];
  return mapper({ ...args, point: adjustedPoint });
};

export const adminSlice = createSlice({
  name: 'admin',
  initialState,
  reducers: {
    setTownId: (state, action: PayloadAction<number | undefined>) => {
      state.townId = action.payload;
      state.buildingId = undefined;
      state.floorId = undefined;
      state.bookingObjectType = undefined;
    },
    setBuildingId: (state, action: PayloadAction<number | undefined>) => {
      state.buildingId = action.payload;
      state.floorId = undefined;
      state.bookingObjectType = undefined;
    },
    setFloorId: (state, action: PayloadAction<number | undefined>) => {
      state.floorId = action.payload;
      state.bookingObjectType = undefined;
      state.backgroundLoadingError = undefined;
    },
    setSelectedTownId: (state, action: PayloadAction<number | undefined>) => {
      state.selectedTownId = action.payload;
    },
    setSelectedBuildingId: (
      state,
      action: PayloadAction<number | undefined>,
    ) => {
      state.selectedBuildingId = action.payload;
    },
    setSelectedFloorId: (state, action: PayloadAction<number | undefined>) => {
      state.selectedFloorId = action.payload;
    },
    setSelectedBookingObject: (
      state,
      action: PayloadAction<BookingObject | undefined>,
    ) => {
      state.selectedPlace = action.payload;
    },
    clearSelectedBookingObject: state => {
      state.selectedPlace = undefined;
    },
    setBookingObjectType: (state, action: PayloadAction<BookingObjectType>) => {
      state.bookingObjectType = action.payload;
    },
    drawGhost: (state, action: PayloadAction<Point>) => {
      if (state.selectedPlace || !state.bookingObjectType) return;

      const adjustedPoint = roundPoint(action.payload);
      const [width, height, fontSize] =
        state.sizesByBookingObjectType[state.bookingObjectType];
      const nextGhost = pointToBookingObject({
        point: adjustedPoint,
        width,
        height,
        fontSize,
        mapPlaceId: 'ghost',
        rotation: state.rotation,
        bookingObjectType: state.bookingObjectType,
      });

      state.ghost = nextGhost;
    },
    removeGhost: state => {
      state.ghost = undefined;
    },
    update: (
      state,
      action: PayloadAction<Partial<Desk> | Partial<Room> | Partial<Parking>>,
    ) => {
      state.selectedPlace = {
        ...state.selectedPlace,
        ...action.payload,
      } as BookingObject;
    },
    moveLeft: state => {
      if (state.selectedPlace) state.selectedPlace.x--;
    },
    moveRight: state => {
      if (state.selectedPlace) state.selectedPlace.x++;
    },
    moveUp: state => {
      if (state.selectedPlace) state.selectedPlace.y--;
    },
    moveDown: state => {
      if (state.selectedPlace) state.selectedPlace.y++;
    },
    move: (state, action: PayloadAction<Point>) => {
      if (state.selectedPlace) {
        const adjustedPoint = roundPoint(action.payload);
        state.selectedPlace.x = adjustedPoint.x;
        state.selectedPlace.y = adjustedPoint.y;
      }
    },
    setParamsByType: (
      state,
      action: PayloadAction<{
        placeType: BookingObjectType;
        rotation: number | undefined;
        width: number;
        height: number;
        fontSize: number;
      }>,
    ) => {
      state.sizesByBookingObjectType[action.payload.placeType] = [
        action.payload.width,
        action.payload.height,
        action.payload.fontSize,
      ];
      if (action.payload.rotation !== undefined)
        state.rotation = action.payload.rotation;
    },
    openTown: state => {
      state.townIsOpened = true;
    },
    openBuilding: state => {
      state.buildingIsOpened = true;
    },
    openFloor: state => {
      state.floorIsOpened = true;
    },
    openPermissions: state => {
      state.permissionsAreOpened = true;
    },
    openBookingObject: state => {
      state.bookingObjectIsOpened = true;
    },
    closeTown: state => {
      state.townIsOpened = false;
    },
    closeBuilding: state => {
      state.buildingIsOpened = false;
    },
    closeFloor: state => {
      state.floorIsOpened = false;
    },
    closePermissions: state => {
      state.permissionsAreOpened = false;
    },
    closeBookingObject: state => {
      state.bookingObjectIsOpened = false;
    },
    setTownIsOpened: (state, action: PayloadAction<boolean>) => {
      state.townIsOpened = action.payload;
    },
    setBuildingIsOpened: (state, action: PayloadAction<boolean>) => {
      state.buildingIsOpened = action.payload;
    },
    setFloorIsOpened: (state, action: PayloadAction<boolean>) => {
      state.floorIsOpened = action.payload;
    },
    setPermissionsAreOpened: (state, action: PayloadAction<boolean>) => {
      state.permissionsAreOpened = action.payload;
    },
    setBookingObjectIsOpened: (state, action: PayloadAction<boolean>) => {
      state.bookingObjectIsOpened = action.payload;
    },
  },
  extraReducers(builder) {
    builder.addCase(fetchAdminAllData.pending, state => {
      state.error = undefined;
      state.loading = true;
    });
    builder.addCase(fetchAdminAllData.rejected, (state, action) => {
      state.error = action.payload;
      state.loading = false;
    });
    builder.addCase(fetchAdminAllData.fulfilled, (state, action) => {
      state.loading = false;
      state.bookingObjects = action.payload.bookingObjects;
      state.backgroundData = action.payload.backgroundData;
    });

    builder.addCase(fetchBookingObjects.fulfilled, (state, action) => {
      state.loading = false;
      state.bookingObjects = action.payload;
    });

    builder.addCase(fetchBookingObject.pending, state => {
      state.placeLoadingError = undefined;
      state.placeLoading = true;
    });
    builder.addCase(fetchBookingObject.rejected, (state, action) => {
      state.placeLoadingError = action.payload;
      state.placeLoading = false;
    });
    builder.addCase(fetchBookingObject.fulfilled, (state, action) => {
      state.placeLoading = false;

      if (state.selectedPlace) {
        state.selectedPlace = { ...state.selectedPlace, ...action.payload };
      }
    });

    builder.addCase(removeTown.fulfilled, (state, action) => {
      if (state.townId === action.meta.arg) {
        state.townId = undefined;
        state.buildingId = undefined;
        state.floorId = undefined;
      }
    });

    builder.addCase(removeBuilding.fulfilled, (state, action) => {
      if (state.buildingId === action.meta.arg) {
        state.buildingId = undefined;
        state.floorId = undefined;
      }
    });

    builder.addCase(removeFloor.fulfilled, (state, action) => {
      if (state.floorId === action.meta.arg) {
        state.floorId = undefined;
      }
    });

    builder.addCase(uploadPlan.pending, state => {
      state.backgroundLoadingError = undefined;
      state.backgroundLoading = true;
    });
    builder.addCase(uploadPlan.rejected, (state, action) => {
      state.backgroundLoadingError = action.payload;
      state.backgroundLoading = false;
    });
  },
});

export const { actions: adminActions, reducer: adminReducer } = adminSlice;

export const openPlace = createAction<BookingObject, 'admin/open'>(
  'admin/open',
);
export const drawPlace = createAction<Point, 'admin/draw'>('admin/draw');

startAppListening({
  predicate: (action, current, previous) => {
    return current.admin.floorId !== previous.admin.floorId;
  },
  effect: async (action, listenerApi) => {
    const { floorId } = getAdminState(listenerApi.getState() as StoreState);
    if (floorId) listenerApi.dispatch(fetchAdminAllData(floorId));
  },
});

startAppListening({
  actionCreator: openPlace,
  effect: (action, listenerApi) => {
    const state = listenerApi.getState();

    const isMobile = getIsMobileWithTouchscreen(state);

    const selectedBookingObject = action.payload;
    if (!selectedBookingObject.id) return;

    listenerApi.dispatch(
      fetchBookingObject({
        id: selectedBookingObject.id,
        type: selectedBookingObject.type,
      }),
    );
    listenerApi.dispatch(
      adminActions.setSelectedBookingObject(selectedBookingObject),
    );
    if (isMobile) {
      listenerApi.dispatch(adminActions.openBookingObject());
    } else {
      listenerApi.dispatch(sidesheetActions.open('adminBookingObject'));
    }
  },
});

startAppListening({
  actionCreator: drawPlace,
  effect: (action, listenerApi) => {
    const state = listenerApi.getState();

    const adminState = getAdminState(state);

    if (!adminState.bookingObjectType) return;

    const isMobile = getIsMobileWithTouchscreen(state);

    const adjustedPoint = roundPoint(action.payload);
    const [width, height, fontSize] =
      adminState.sizesByBookingObjectType[adminState.bookingObjectType];
    const place = pointToBookingObject({
      point: adjustedPoint,
      width,
      height,
      fontSize,
      mapPlaceId: crypto.randomUUID(),
      rotation: adminState.rotation,
      bookingObjectType: adminState.bookingObjectType,
    });

    if (!place) return;

    listenerApi.dispatch(
      adminActions.setSelectedBookingObject(toInitialBookingObject(place)),
    );
    listenerApi.dispatch(adminActions.removeGhost());
    if (isMobile) {
      listenerApi.dispatch(adminActions.openBookingObject());
    } else {
      listenerApi.dispatch(sidesheetActions.open('adminBookingObject'));
    }
  },
});

startAppListening({
  predicate: (action, current, previous) => {
    return (
      current.sidesheet.current !== 'adminBookingObject' &&
      previous.sidesheet.current === 'adminBookingObject'
    );
  },
  effect: (action, listenerApi) => {
    listenerApi.dispatch(adminActions.clearSelectedBookingObject());
  },
});
